import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { AuthContext } from '../../context/AuthContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {
  FiBriefcase, FiUsers, FiBook, FiAward, FiHelpCircle, FiCheck, FiInfo,
  FiArrowRight, FiTrendingUp, FiTrendingDown, FiActivity, FiDollarSign,
  FiUserPlus, FiTarget, FiZap, FiCalendar, FiClock, FiAlertCircle,
  FiBarChart2, FiPieChart, FiGrid, FiLayers, FiGift, FiStar,
  FiMessageSquare, FiShoppingBag, FiAnchor, FiPackage, FiThumbsUp,
  FiEye, FiHeart, FiShare2, FiDownload, FiUpload, FiRefreshCw,
  FiFilter, FiSearch, FiMenu, FiX, FiChevronRight, FiChevronDown
} = FiIcons;

const PortalDashboard = () => {
  const { currentUser, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [timeRange, setTimeRange] = useState('7d');
  const [activeChart, setActiveChart] = useState('revenue');
  const [refreshing, setRefreshing] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  // Real-time data simulation
  const [realtimeData, setRealtimeData] = useState({
    activeUsers: 1247,
    revenue: 84320,
    conversions: 342,
    engagement: 78.5
  });

  // Simulate real-time updates
  useEffect(() => {
    const interval = setInterval(() => {
      setRealtimeData(prev => ({
        activeUsers: prev.activeUsers + Math.floor(Math.random() * 10 - 5),
        revenue: prev.revenue + Math.floor(Math.random() * 500 - 200),
        conversions: prev.conversions + Math.floor(Math.random() * 5 - 2),
        engagement: Math.min(100, Math.max(0, prev.engagement + (Math.random() * 2 - 1)))
      }));
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const portalData = [
    {
      id: 'internal',
      title: 'Internal Portal',
      description: 'Command center for staff, case management, and operations.',
      features: [
        'Referral & case management',
        'User administration',
        'Analytics & reporting'
      ],
      icon: FiBriefcase,
      color: 'from-blue-500 to-blue-700',
      path: '/admin',
      stats: { users: 245, active: 189, growth: 12.5 }
    },
    {
      id: 'community',
      title: 'Community Portal',
      description: 'Connect, share, and learn from the professional community.',
      features: [
        'Discussion forums',
        'Member directory',
        'Experience sharing'
      ],
      icon: FiUsers,
      color: 'from-green-500 to-emerald-700',
      path: '/community',
      stats: { users: 3420, active: 2890, growth: 18.3 }
    },
    {
      id: 'blog',
      title: 'Blog & News',
      description: 'Industry insights and updates.',
      features: [
        'Latest articles',
        'Case studies',
        'Thought leadership'
      ],
      icon: FiBook,
      color: 'from-purple-500 to-purple-700',
      path: '/blog',
      stats: { users: 5670, active: 4320, growth: 22.7 }
    },
    {
      id: 'education',
      title: 'Education Portal',
      description: 'LMS and CE system.',
      features: [
        'Online courses',
        'CE credits',
        'SCORM video lessons'
      ],
      icon: FiAward,
      color: 'from-yellow-500 to-orange-600',
      path: '/lms',
      stats: { users: 1890, active: 1240, growth: 15.2 }
    },
    {
      id: 'help',
      title: 'Help Center',
      description: 'Documentation, tutorials, and support.',
      features: [
        'User guides',
        'Video tutorials',
        'FAQs & support'
      ],
      icon: FiHelpCircle,
      color: 'from-indigo-500 to-indigo-700',
      path: '/help',
      stats: { users: 980, active: 445, growth: 8.9 }
    }
  ];

  const analyticsData = {
    revenue: [
      { label: 'Mon', value: 12400 },
      { label: 'Tue', value: 15800 },
      { label: 'Wed', value: 18200 },
      { label: 'Thu', value: 16500 },
      { label: 'Fri', value: 21300 },
      { label: 'Sat', value: 19800 },
      { label: 'Sun', value: 17200 }
    ],
    traffic: [
      { label: 'Mon', value: 3200 },
      { label: 'Tue', value: 4100 },
      { label: 'Wed', value: 3800 },
      { label: 'Thu', value: 4500 },
      { label: 'Fri', value: 5200 },
      { label: 'Sat', value: 4800 },
      { label: 'Sun', value: 4200 }
    ],
    conversion: [
      { label: 'Mon', value: 45 },
      { label: 'Tue', value: 52 },
      { label: 'Wed', value: 48 },
      { label: 'Thu', value: 58 },
      { label: 'Fri', value: 65 },
      { label: 'Sat', value: 61 },
      { label: 'Sun', value: 55 }
    ]
  };

  const kpiCards = [
    {
      title: 'Active Users',
      value: realtimeData.activeUsers.toLocaleString(),
      change: '+12.5%',
      trend: 'up',
      icon: FiUsers,
      color: 'from-blue-500 to-cyan-500',
      description: 'Currently online'
    },
    {
      title: 'Total Revenue',
      value: `$${(realtimeData.revenue / 1000).toFixed(1)}K`,
      change: '+23.8%',
      trend: 'up',
      icon: FiDollarSign,
      color: 'from-green-500 to-emerald-500',
      description: 'This week'
    },
    {
      title: 'Conversions',
      value: realtimeData.conversions.toLocaleString(),
      change: '+8.3%',
      trend: 'up',
      icon: FiTarget,
      color: 'from-purple-500 to-pink-500',
      description: 'Today'
    },
    {
      title: 'Engagement',
      value: `${realtimeData.engagement.toFixed(1)}%`,
      change: '-2.1%',
      trend: 'down',
      icon: FiActivity,
      color: 'from-orange-500 to-red-500',
      description: 'Avg session time'
    }
  ];

  const recentActivity = [
    { user: 'Sarah Johnson', action: 'completed course', item: 'Advanced Analytics', time: '2 min ago', avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop', color: 'green' },
    { user: 'Mike Chen', action: 'joined dealroom', item: 'Patient Case #4521', time: '5 min ago', avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop', color: 'blue' },
    { user: 'Emma Wilson', action: 'earned reward', item: '500 points', time: '12 min ago', avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop', color: 'purple' },
    { user: 'David Lee', action: 'submitted claim', item: 'Insurance Claim #892', time: '18 min ago', avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop', color: 'orange' },
    { user: 'Lisa Brown', action: 'created post', item: 'Community Discussion', time: '25 min ago', avatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100&h=100&fit=crop', color: 'pink' }
  ];

  const topPerformers = [
    { name: 'Advanced Analytics Course', category: 'Education', value: 1245, growth: 28.5, icon: FiAward, color: 'purple' },
    { name: 'Patient Dealrooms', category: 'Internal', value: 892, growth: 15.2, icon: FiBriefcase, color: 'blue' },
    { name: 'Community Forum', category: 'Community', value: 3421, growth: 42.8, icon: FiMessageSquare, color: 'green' },
    { name: 'Rewards Program', category: 'Engagement', value: 567, growth: 19.4, icon: FiGift, color: 'orange' }
  ];

  const handleRefresh = () => {
    setRefreshing(true);
    setTimeout(() => setRefreshing(false), 1000);
  };

  const handlePortalSelect = (portal) => {
    navigate(portal.path);
  };

  const handleAnalyticsClick = () => {
    navigate('/analytics');
  };

  const getMaxValue = (data) => Math.max(...data.map(d => d.value));

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-50">
      <div className="max-w-[1600px] mx-auto p-6 space-y-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col lg:flex-row lg:items-center lg:justify-between"
        >
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2">
              Welcome back, {currentUser?.name || user?.name || 'User'}! 👋
            </h1>
            <p className="text-lg text-gray-600">
              Here's what's happening with your platform today
            </p>
          </div>
          <div className="mt-4 lg:mt-0 flex items-center space-x-3">
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className="px-4 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="24h">Last 24 Hours</option>
              <option value="7d">Last 7 Days</option>
              <option value="30d">Last 30 Days</option>
              <option value="90d">Last 90 Days</option>
            </select>
            <button
              onClick={handleRefresh}
              className="p-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <SafeIcon icon={FiRefreshCw} className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={handleAnalyticsClick}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
            >
              <SafeIcon icon={FiBarChart2} className="w-4 h-4" />
              <span>Full Analytics</span>
            </button>
          </div>
        </motion.div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {kpiCards.map((kpi, index) => (
            <motion.div
              key={kpi.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-lg transition-shadow"
            >
              <div className="flex items-center justify-between mb-4">
                <div className={`p-3 bg-gradient-to-br ${kpi.color} rounded-lg`}>
                  <SafeIcon icon={kpi.icon} className="w-6 h-6 text-white" />
                </div>
                <div className={`flex items-center space-x-1 text-sm font-semibold ${
                  kpi.trend === 'up' ? 'text-green-600' : 'text-red-600'
                }`}>
                  <SafeIcon icon={kpi.trend === 'up' ? FiTrendingUp : FiTrendingDown} className="w-4 h-4" />
                  <span>{kpi.change}</span>
                </div>
              </div>
              <h3 className="text-sm font-medium text-gray-600 mb-1">{kpi.title}</h3>
              <p className="text-3xl font-bold text-gray-900 mb-1">{kpi.value}</p>
              <p className="text-xs text-gray-500">{kpi.description}</p>
              <div className="mt-4 h-1 bg-gray-100 rounded-full overflow-hidden">
                <motion.div
                  className={`h-full bg-gradient-to-r ${kpi.color}`}
                  initial={{ width: 0 }}
                  animate={{ width: `${Math.random() * 40 + 60}%` }}
                  transition={{ duration: 1, delay: index * 0.1 }}
                />
              </div>
            </motion.div>
          ))}
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Chart */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-100 p-6"
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-bold text-gray-900">Revenue Analytics</h3>
                <p className="text-sm text-gray-600">Weekly performance overview</p>
              </div>
              <div className="flex items-center space-x-2">
                {['revenue', 'traffic', 'conversion'].map((chart) => (
                  <button
                    key={chart}
                    onClick={() => setActiveChart(chart)}
                    className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                      activeChart === chart
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {chart.charAt(0).toUpperCase() + chart.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            {/* Bar Chart */}
            <div className="h-64 flex items-end justify-between space-x-2">
              {analyticsData[activeChart].map((item, index) => {
                const maxValue = getMaxValue(analyticsData[activeChart]);
                const height = (item.value / maxValue) * 100;
                
                return (
                  <div key={item.label} className="flex-1 flex flex-col items-center">
                    <motion.div
                      initial={{ height: 0 }}
                      animate={{ height: `${height}%` }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      className="w-full bg-gradient-to-t from-blue-500 to-cyan-400 rounded-t-lg relative group cursor-pointer hover:from-blue-600 hover:to-cyan-500 transition-all"
                    >
                      <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white px-2 py-1 rounded text-xs opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                        {activeChart === 'revenue' ? `$${item.value.toLocaleString()}` : item.value.toLocaleString()}
                      </div>
                    </motion.div>
                    <p className="text-xs text-gray-600 mt-2 font-medium">{item.label}</p>
                  </div>
                );
              })}
            </div>

            {/* Chart Legend */}
            <div className="mt-6 pt-6 border-t border-gray-200 flex items-center justify-between">
              <div className="flex items-center space-x-6">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-gradient-to-r from-blue-500 to-cyan-400 rounded-full" />
                  <span className="text-sm text-gray-600">Current Period</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-gray-300 rounded-full" />
                  <span className="text-sm text-gray-600">Previous Period</span>
                </div>
              </div>
              <button 
                onClick={handleAnalyticsClick}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center space-x-1"
              >
                <span>View Details</span>
                <SafeIcon icon={FiChevronRight} className="w-4 h-4" />
              </button>
            </div>
          </motion.div>

          {/* Top Performers */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-white rounded-xl shadow-sm border border-gray-100 p-6"
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-bold text-gray-900">Top Performers</h3>
                <p className="text-sm text-gray-600">This week</p>
              </div>
              <SafeIcon icon={FiStar} className="w-5 h-5 text-yellow-500" />
            </div>

            <div className="space-y-4">
              {topPerformers.map((item, index) => (
                <motion.div
                  key={item.name}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.6 + index * 0.1 }}
                  className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  <div className={`p-2 bg-${item.color}-100 rounded-lg`}>
                    <SafeIcon icon={item.icon} className={`w-4 h-4 text-${item.color}-600`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">{item.name}</p>
                    <p className="text-xs text-gray-500">{item.category}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold text-gray-900">{item.value}</p>
                    <p className="text-xs text-green-600 font-medium">+{item.growth}%</p>
                  </div>
                </motion.div>
              ))}
            </div>

            <button 
              onClick={handleAnalyticsClick}
              className="w-full mt-4 py-2 text-sm text-blue-600 hover:text-blue-700 font-medium border-t border-gray-200 pt-4"
            >
              View All Performance Metrics →
            </button>
          </motion.div>
        </div>

        {/* Portal Cards & Activity Feed */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Portal Cards */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="mb-6"
            >
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Your Workspaces</h2>
              <p className="text-gray-600">Select a portal to access specialized tools and features</p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {portalData.map((portal, index) => (
                <motion.div
                  key={portal.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.7 + index * 0.1 }}
                  whileHover={{ y: -5, scale: 1.02 }}
                  className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden cursor-pointer hover:shadow-xl transition-all"
                  onClick={() => handlePortalSelect(portal)}
                >
                  <div className={`h-2 bg-gradient-to-r ${portal.color}`} />
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className={`p-3 bg-gradient-to-br ${portal.color} rounded-xl`}>
                        <SafeIcon icon={portal.icon} className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex items-center space-x-1 text-xs text-green-600 font-semibold">
                        <SafeIcon icon={FiTrendingUp} className="w-3 h-3" />
                        <span>+{portal.stats.growth}%</span>
                      </div>
                    </div>

                    <h3 className="text-lg font-bold text-gray-900 mb-2">{portal.title}</h3>
                    <p className="text-sm text-gray-600 mb-4">{portal.description}</p>

                    <ul className="space-y-2 mb-4">
                      {portal.features.map((feature, i) => (
                        <li key={i} className="flex items-center space-x-2 text-xs text-gray-700">
                          <SafeIcon icon={FiCheck} className="w-3 h-3 text-green-500 flex-shrink-0" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>

                    <div className="pt-4 border-t border-gray-200 flex items-center justify-between">
                      <div>
                        <p className="text-xs text-gray-500">Active Users</p>
                        <p className="text-sm font-bold text-gray-900">{portal.stats.active.toLocaleString()}</p>
                      </div>
                      <button className="px-4 py-2 bg-gray-900 text-white text-sm rounded-lg hover:bg-gray-800 transition-colors flex items-center space-x-1">
                        <span>Enter</span>
                        <SafeIcon icon={FiArrowRight} className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Recent Activity */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 h-fit"
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-bold text-gray-900">Live Activity</h3>
                <p className="text-sm text-gray-600">Real-time updates</p>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                <span className="text-xs text-gray-600">Live</span>
              </div>
            </div>

            <div className="space-y-4 max-h-[600px] overflow-y-auto">
              <AnimatePresence>
                {recentActivity.map((activity, index) => (
                  <motion.div
                    key={`${activity.user}-${index}`}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-start space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <img
                      src={activity.avatar}
                      alt={activity.user}
                      className="w-10 h-10 rounded-full object-cover border-2 border-gray-200"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-900">
                        <span className="font-semibold">{activity.user}</span>
                        {' '}
                        <span className="text-gray-600">{activity.action}</span>
                      </p>
                      <p className="text-sm text-gray-700 font-medium mt-0.5">{activity.item}</p>
                      <p className="text-xs text-gray-500 mt-1">{activity.time}</p>
                    </div>
                    <div className={`w-2 h-2 bg-${activity.color}-500 rounded-full mt-2`} />
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>

            <button className="w-full mt-4 py-2 text-sm text-blue-600 hover:text-blue-700 font-medium border-t border-gray-200 pt-4">
              View All Activity →
            </button>
          </motion.div>
        </div>

        {/* Quick Actions & Help */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9 }}
          className="bg-gradient-to-r from-blue-600 to-cyan-600 rounded-xl shadow-lg p-8 text-white"
        >
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
            <div className="mb-6 lg:mb-0">
              <h3 className="text-2xl font-bold mb-2">Need Help Getting Started?</h3>
              <p className="text-blue-100 mb-4">
                Explore our comprehensive guides, video tutorials, and documentation to make the most of your platform.
              </p>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={() => navigate('/help')}
                  className="px-5 py-2.5 bg-white text-blue-600 rounded-lg hover:bg-blue-50 transition-colors font-semibold flex items-center space-x-2"
                >
                  <SafeIcon icon={FiHelpCircle} className="w-4 h-4" />
                  <span>Visit Help Center</span>
                </button>
                <button
                  onClick={() => navigate('/lms')}
                  className="px-5 py-2.5 bg-blue-500 text-white rounded-lg hover:bg-blue-400 transition-colors font-semibold flex items-center space-x-2"
                >
                  <SafeIcon icon={FiBook} className="w-4 h-4" />
                  <span>Browse Courses</span>
                </button>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="hidden lg:block w-32 h-32 bg-white/10 rounded-full flex items-center justify-center backdrop-blur-sm">
                <SafeIcon icon={FiZap} className="w-16 h-16 text-white" />
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PortalDashboard;